import React, { useEffect, useState } from "react";
import axios from "axios";
import { Users, ChevronRight, Mail, Phone, Building, Calendar, FileText, DollarSign, CheckCircle, ArrowLeft, Loader2, LogIn } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Users tab for the Admin Panel.
 *   • Lists every registered user with a mini metrics row (cases, payout).
 *   • Clicking a user opens their full read-only dashboard — identical to
 *     what the user sees in UserDashboard but gated behind the admin's
 *     OTP-verified Blexbot session (`X-PV-Session`).
 *
 * `onAuthError(err)` is invoked by the parent `AdminPanel` on any 401 so
 * the panel can flip into its "please verify again via WhatsApp" state.
 */
export default function AdminUsers({ onAuthError, onImpersonate, onOpenUserCase }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null); // { user, metrics, cases }
  const [selectedLoading, setSelectedLoading] = useState(false);

  const sessionHeaders = () => {
    try {
      return { "X-PV-Session": sessionStorage.getItem("pv_session_token") || "" };
    } catch {
      return { "X-PV-Session": "" };
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const resp = await axios.get(`${API}/admin/users`, { headers: sessionHeaders() });
        setUsers(resp.data?.users || []);
      } catch (err) {
        if (onAuthError && onAuthError(err)) return;
        setError(err?.response?.data?.detail || "Failed to load users.");
      } finally {
        setLoading(false);
      }
    })();
  }, [onAuthError]);

  const openUser = async (username) => {
    try {
      setSelectedLoading(true);
      setError("");
      const resp = await axios.get(
        `${API}/admin/users/${encodeURIComponent(username)}/dashboard`,
        { headers: sessionHeaders() }
      );
      setSelected(resp.data);
    } catch (err) {
      if (onAuthError && onAuthError(err)) return;
      setError(err?.response?.data?.detail || "Failed to load user dashboard.");
    } finally {
      setSelectedLoading(false);
    }
  };

  const fmtMoney = (v) =>
    (typeof v === "number" ? v : parseFloat(v) || 0).toLocaleString("en-CA", {
      style: "currency",
      currency: "CAD",
    });
  const fmtDate = (s) => {
    if (!s) return "—";
    try {
      return new Date(s).toLocaleDateString();
    } catch {
      return String(s).slice(0, 10);
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "requires_attention":
        return "bg-amber-100 text-amber-800";
      case "researching":
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "draft":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // ───────────── Per-user drill-down ─────────────
  if (selected) {
    const u = selected.user || {};
    const m = selected.metrics || {};
    return (
      <Card className="mb-8" data-testid="admin-user-detail">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected(null)}
              className="mb-2 -ml-2 text-cyan-700"
              data-testid="admin-users-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to all users
            </Button>
            <CardTitle className="text-xl text-[#1E3A5F]">{u.username}</CardTitle>
            <div className="mt-2 text-sm text-gray-600 flex flex-wrap gap-x-5 gap-y-1">
              {u.email && (
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4" /> {u.email}
                </span>
              )}
              {u.company && (
                <span className="flex items-center gap-1">
                  <Building className="w-4 h-4" /> {u.company}
                </span>
              )}
              {u.whatsapp_number && (
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />+{u.whatsapp_country_code}{" "}
                  {u.whatsapp_number}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" /> Joined {fmtDate(u.created_at)}
              </span>
            </div>
          </div>
          {onImpersonate && (
            <Button
              onClick={() => onImpersonate(u.username)}
              data-testid="admin-open-user-dashboard"
              className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full px-5 shadow-md"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Open dashboard
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {/* Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="rounded-xl border border-cyan-100 bg-cyan-50 p-4">
              <div className="text-xs uppercase tracking-wide text-cyan-700 font-semibold flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> Total cases
              </div>
              <div className="text-2xl font-bold text-[#1E3A5F] mt-1">
                {m.total_cases ?? 0}
              </div>
            </div>
            <div className="rounded-xl border border-green-100 bg-green-50 p-4">
              <div className="text-xs uppercase tracking-wide text-green-700 font-semibold flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5" /> Completed
              </div>
              <div className="text-2xl font-bold text-[#1E3A5F] mt-1">
                {m.completed_cases ?? 0}
              </div>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
              <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold flex items-center gap-1">
                <DollarSign className="w-3.5 h-3.5" /> Total payout
              </div>
              <div className="text-2xl font-bold text-[#1E3A5F] mt-1">
                {fmtMoney(m.total_payout ?? 0)}
              </div>
            </div>
          </div>

          {/* Case list */}
          <h4 className="font-semibold text-gray-700 mb-3">All cases</h4>
          {(selected.cases || []).length === 0 ? (
            <p className="text-sm text-gray-500">No cases yet.</p>
          ) : (
            <div className="divide-y border rounded-lg overflow-hidden">
              {selected.cases.map((c) => (
                <div
                  key={c.id}
                  data-testid={`admin-user-case-${c.id}`}
                  onClick={() => onOpenUserCase && onOpenUserCase(selected.user?.username, c.id)}
                  role={onOpenUserCase ? "button" : undefined}
                  tabIndex={onOpenUserCase ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (!onOpenUserCase) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpenUserCase(selected.user?.username, c.id);
                    }
                  }}
                  className={`flex items-center justify-between p-4 hover:bg-cyan-50/60 transition ${onOpenUserCase ? "cursor-pointer" : ""}`}
                >
                  <div>
                    <div className="font-medium text-[#1E3A5F]">
                      {c.title || "Untitled case"}
                    </div>
                    <div className="text-xs text-gray-500 flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                      <span>Case {c.case_number}</span>
                      {c.blexbot_request_number && (
                        <span className="inline-flex items-center text-[10px] font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 px-2 py-0.5 rounded-full">
                          ID # {c.blexbot_request_number}
                        </span>
                      )}
                      <span>• {fmtDate(c.created_at)}</span>
                      <span>• {c.total_items} items</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm font-bold text-green-700">
                        {fmtMoney(c.effective_payout || c.total_payout || 0)}
                      </div>
                    </div>
                    <Badge className={statusColor(c.status)}>{c.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // ───────────── Users list ─────────────
  return (
    <Card className="mb-8" data-testid="admin-users-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl text-[#1E3A5F]">
          <Users className="w-5 h-5" /> Users ({users.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
          </div>
        ) : users.length === 0 ? (
          <p className="text-sm text-gray-500">No registered users yet.</p>
        ) : selectedLoading ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Opening dashboard…
          </div>
        ) : (
          <div className="divide-y border rounded-lg overflow-hidden">
            {users.map((u) => (
              <div
                key={u.username}
                data-testid={`admin-user-row-${u.username}`}
                className="flex items-center justify-between p-4 hover:bg-cyan-50/60 transition"
              >
                <button
                  type="button"
                  onClick={() => openUser(u.username)}
                  className="flex-1 text-left"
                >
                  <div className="font-semibold text-[#1E3A5F]">{u.username}</div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                    {u.email && <span>{u.email}</span>}
                    {u.company && <span>{u.company}</span>}
                    <span>Joined {fmtDate(u.created_at)}</span>
                  </div>
                </button>
                <div className="flex items-center gap-5">
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Cases</div>
                    <div className="font-bold text-[#1E3A5F]">
                      {u.metrics?.total_cases ?? 0}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Payout</div>
                    <div className="font-bold text-green-700">
                      {fmtMoney(u.metrics?.total_payout ?? 0)}
                    </div>
                  </div>
                  {onImpersonate && (
                    <Button
                      onClick={() => onImpersonate(u.username)}
                      size="sm"
                      data-testid={`admin-open-dashboard-${u.username}`}
                      className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full px-4 shadow-sm"
                    >
                      <LogIn className="w-3.5 h-3.5 mr-1.5" />
                      Open dashboard
                    </Button>
                  )}
                  <ChevronRight className="w-5 h-5 text-cyan-500" />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
